/**
 * Data Model for Services and Projects
 * 
 * This file contains the data structures for:
 * - Services (with references to projects)
 * - Projects (detailed project information)
 * 
 * Static data structure designed for a React static site.
 */

// Projects data structure
export const projects = [
  // Data & Analytics Engineering Projects
  {
    id: "crain-communications",
    client_name: "Crain Communications",
    title: "Data Warehouse & Reporting",
    logo: "/project-logos/crain-communications.jpg",
    problem_statement: "Crain Communications had a nascent data warehouse and legacy systems resulted in unreliable data delivery, low confidence in insights, and lack of breadth in analytic capabilities. The team wasn't trained on modern data stack best practices.",
    solution: "Implemented modern data warehouse architecture in Lambda, Glue, Redshift, and DBT. Rebuilt ETL pipelines from scratch, improving the reliability and creating thorough documentation. Trained team on best practices.",
    details: [
      "Rebuilt legacy ETL pipelines from scratch in AWS and DBT",
      "Implemented data governance and validation frameworks",
      "Created comprehensive documentation and training materials",
      "Trained team on modern data stack best practices",
      "Expanded data delivery to the data warehouse to cover all key sources"
    ]
  },
  {
    id: "hart-energy",
    client_name: "Hart Energy",
    title: "Data Warehouse & Reporting",
    logo: "/project-logos/hart-energy.png",
    problem_statement: "Hart Energy had no existing data infrastructure and relied exclusively on data connections between external platforms. They lacked revenue reporting, flexible analyses, and all analyses were highly manual because they relied on manual exports and joins between platforms like Cvent (conferences) and Stripe (subscriptions).",
    solution: "Built an affordable AWS data stack without relying on budget increases or new platforms. Created end-to-end data pipeline for key data assets into a centralized warehouse. Built the reporting suite used by the entire company to evaluate their subscription and conference businesses. Helped the team shift to an OKR-driven culture.",
    details: [
      "Designed data lake architecture on AWS",
      "Integrated multiple external data sources (Cvent, Stripe, Google Analytics)",
      "Built the end-to-end reporting suite for subscription and conference revenue analysis",
      "Helped the team shift to an OKR-driven culture"
    ]
  },
  {
    id: "mastercard",
    client_name: "Mastercard",
    title: "Business Experimentation",
    logo: "/project-logos/mastercard.png",
    problem_statement: "With an excess of Q4 projects, the Mastercard team needed support to deliver high-quality analyses to their clients with minimal supervision or overhead.",
    solution: "Quickly spun up on projects and managed junior Mastercard employees to deliver $200K+ of analytics projects to clients.",
    details: [
      "Collaborated with Account Managers and Sales to define project scopes",
      "Managed and mentored junior analysts to deliver high-quality work",
      "Delivered actionable insights that drove client decision-making",
      "Ensured projects were completed on time and within budget",
    ]
  },
  {
    id: "amplify-data",
    client_name: "Amplify Data",
    title: "Multi-Client Data Platform",
    logo: "/project-logos/amplify-data.png",
    problem_statement: "Amplify Data needed to build a scalable platform to validate and visualize data.",
    solution: "Partnered with the CTO to develop a robust data validation API that would scale across their future clients. Ultimately, this foundational work would help Amplify acquire its first clients and eventually get acquired by Monda.",
    details: [
      "Architected multi-tenant data warehouse",
      "Developed robust data quality and validation checks",
      "Built automated data quality monitoring",
      "Created data quality reports and graphs",
    ]
  },

  // Applied AI & Machine Learning Projects
  {
    id: "insurity",
    client_name: "Insurity",
    title: "Neural Network Modeling",
    logo: "/project-logos/insurity.png",
    problem_statement: "Legacy models for property-level flood risk prediction were inaccurate and couldn't capture complex patterns in the data or anomalous behavior.",
    solution: "Developed an industry-grade neural network based flood risk model by aggregating both public and private data sources.",
    details: [
      "Built ETLs to aggregated data from multiple sources (weather, topography, structural, hydrography)",
      "Built a comprehensive feature-creation tool to allow for flexible model feature generation",
      "Created flexible neural network models platform to predict flood risk",
      "Established a process to scale the flood risk model to other hazards (wildfire, earthquake)",
      "100% more efficient than industry-standard models at predicting floods in regions where a flood had not previously occurred"
    ]
  },
  {
    id: "solidly-meet",
    client_name: "Solidly Meet",
    title: "AI Meeting Assistant",
    icon: "🤝",
    logo: "/project-logos/solidly-logo.png",
    problem_statement: "Remote teams struggled with meeting efficiency, follow-up actions, and knowledge retention from discussions. Existing AI meeting assistants were either inaccurate or too expensive.",
    solution: "Built pay-as-you-go AI-powered meeting assistant that transcribes, summarizes, and extracts action items.",
    details: [
      "Developed AI transcription with proprietary speaker identification algorithms",
      "Implemented AI summarization and action item extraction",
      "Created intelligent meeting search and knowledge base",
      "Launched and marketed the product with pay-as-you-go pricing",
    ]
  },
  {
    id: "good-cents",
    client_name: "Good Cents",
    title: "Personal Finance AI Advisor",
    icon: "💰",
    logo: "/project-logos/good-cents.png",
    problem_statement: "Good Cents needed a technical partner to help them create a text message based AI financial advisor that would provide accessible and personalized financial guidance.",
    solution: "Created a text message based AI financial advisor that would provide accessible and personalized financial guidance.",
    details: [
      "Built the core data architecture behind the AI advisor",
      "Implemented an agentic AI framework to handle multi-step financial queries",
      "Integrated with SMS platforms for 24/7 text-based user experience",
      "Collaborated with founder to launch the product and ensure reliability for initial users",
    ]
  },

  // Productization & Prototyping Projects
  {
    id: "game-boost-analytics",
    client_name: "Game Boost Analytics",
    title: "AI Video Game Coach",
    logo: "/project-logos/game-boost-analytics.png",
    problem_statement: "Gamers lacked effective systems to improve their gameplay. Personalized coaching was expensive and time-consuming, and existing analytics tools failed to provide actionable insights due to low data availability.",
    solution: "Developed an AI video game coach which would watch you play, analyze your gameplay, and give you insights for how you could improve.",
    details: [
      "Built video ingestions processes through Twitch APIs",
      "Developed computer vision models for gameplay analysis",
      "Created intuitive dashboard for players to analyze their performance",
      "Implemented data-driven video generation to highlight key moments where players did well or could improve",
    ]
  },
  {
    id: "solidly-finance",
    client_name: "Solidly Finance",
    title: "Financial Planning Desktop App",
    logo: "/project-logos/solidly-finance.png",
    problem_statement: "There wasn't a financial planning app that met the needs of privacy-conscious users. They all required cloud storage and the sharing of access to sensitive financial data.",
    solution: "Built comprehensive desktop-based financial planning application.",
    details: [
      "Created full-featured desktop application for financial planning",
      "Developed light-cloud framework for handling payments and initial user authentication",
      "Leveraged AI copilots to build and launch marketing site",
    ]
  },
  {
    id: "augment-analytics",
    client_name: "Augment Analytics",
    title: "Talent Matching AI",
    logo: "/project-logos/augment-analytics.png",
    problem_statement: "Augment Analytics needed AI to help more effectively and efficiently match Augment's large talent bench with client needs.",
    solution: "Developed an AI-driven talent matching platform to connect clients with the right members of the Augment team.",
    details: [
      "Integrated with the existing Augment resume database",
      "Created a user-friendly interface to submit client requests",
      "Coordinated with Augment team to validate and test matches on live projects, ensuring high satisfaction",
    ]
  }
];

// Testimonials data structure
export const testimonials = [
  {
    id: "hart-energy-testimonial-1",
    project_id: "hart-energy",
    name: "Stacy Eisner",
    title: "Chief Digital Officer",
    picture: "/testimonial-images/stacy.jpg",
    body: "Alek joined Hart Energy as a BI consultant and quickly took on a more impactful role. With a tactful and collaborative approach, Alek became a trusted advisor to each of my team members. He identified areas of opportunity and documented, trained, and partnered with the team to lead by example and upskill their performance. Alek has been an indispensable asset to my organization through a challenging cultural transition and his influence will translate into business results for the future."
  },
  {
    id: "hart-energy-testimonial-2",
    project_id: "hart-energy",
    name: "Derick Baker",
    title: "VP of Digital",
    picture: "/testimonial-images/derick.jpg",
    body: "Alek is an indispensable asset for the Hart Energy Digital team. His impact goes beyond building dashboards and sharing insights. He's enabled us to become a data-driven company and, fueled by his insights, our revenue has soared higher than it has ever been. He is highly organized, a great communicator, and capable of managing the kinds of large-scale projects that push our business forward. Hiring Alek is one of the most intelligent business decisions I could have made."
  },
  {
    id: "amplify-data-testimonial-1",
    project_id: "amplify-data",
    name: "Ameya Pathare",
    title: "Chief Technology Officer",
    picture: "/testimonial-images/ameya.jpg",
    body: "Alek built a reliable infrastructure to handle key data visualizations for our product. He partnered with us throughout the development process to quickly understand our needs and build a solution to support our enterprise clients."
  },
  {
    id: "good-cents-testimonial-1",
    project_id: "good-cents",
    name: "Stealth Founder",
    title: "Founder & CEO",
    picture: "/testimonial-images/placeholder.jpg",
    body: "Good Cents engaged Alek to build the first launchable version of our product. I wanted Good Cents built efficiently and according to best practices. Alek made that happen. He leveraged his connections to find a capable developer. After we aligned on a scope of work, he worked closely with the developer to ensure the product met my budget, requirements, and standards. I enjoyed working with Alek, and I fully trust him and the quality of his work."
  },
  {
    id: "augment-analytics-testimonial-1",
    project_id: "augment-analytics",
    name: "Brett Ludden",
    title: "Co-founder & Managing Partner",
    picture: "/testimonial-images/brett.jpg",
    body: "Alek partnered with Augment to help us understand whether AI could streamline some of our project intake processes. He collaborated with me to define a cost-affordable MVP we executed in only a few weeks."
  },
  {
    id: "insurity-testimonial-1",
    project_id: "insurity",
    name: "Jatin Atre, Ph.D.",
    title: "Chief Business Officer",
    picture: "/testimonial-images/jatin.jpg",
    body: "We wanted to learn if neural networks would be an effective way to predict unprecedented property-level flood risk. Alek did an amazing job of quickly developing the neural network model. Within weeks, we had a functioning prototype. As he progressively layered in additional model features, the performance continued to improve, eventually surpassing many of the industry-standard predictive models."
  },
];

// Services data structure with project references
export const services = [
  {
    id: "data-analytics-engineering",
    title: "Data & Analytics Engineering",
    icon: "📊",
    image: "/service-images/data-engineering.jpg",
    description: "Modern data warehouse architecture, governance, and real-time pipelines that power decision-making.",
    features: [
      "Modern data warehouse architecture (Redshift, BigQuery, Snowflake, DBT, Fivetran)",
      "Data validation, governance, and visualization",
      "Incremental and real-time data pipelines",
      "Enterprise data lake and warehouse design"
    ],
    project_ids: [
      "crain-communications",
      "mastercard",
      "hart-energy", 
      "amplify-data"
    ]
  },
  {
    id: "applied-ai-ml",
    title: "Applied AI & Machine Learning",
    icon: "🤖",
    image: "/service-images/ai.jpg",
    description: "Production-ready AI systems that deliver measurable business outcomes, not just impressive demos.",
    features: [
      "Predictive modeling and neural networks for business outcomes",
      "AI copilots and agents for internal productivity",
      "Natural language understanding, summarization, and automation",
      "Computer vision and document processing"
    ],
    project_ids: [
      "augment-analytics",
      "good-cents",
      "insurity",
      "solidly-meet",
    ]
  },
  {
    id: "productization-prototyping",
    title: "Productization & Prototyping",
    icon: "🚀",
    image: "/service-images/prototype.jpg",
    description: "From prototype to production — AI applications that users actually adopt and businesses rely on.",
    features: [
      "MVP AI app design and launch",
      "Human-AI interface design and UX",
      "Private and on-premise AI solutions for enterprise",
      "Full-stack development and deployment"
    ],
    project_ids: [
      "game-boost-analytics",
      "augment-analytics",
      "solidly-finance",
      "good-cents",
      "solidly-meet"
    ]
  }
];

// Helper functions for data access
export const getServiceById = (serviceId) => {
  return services.find(service => service.id === serviceId);
};

export const getProjectById = (projectId) => {
  return projects.find(project => project.id === projectId);
};

export const getProjectsForService = (serviceId) => {
  const service = getServiceById(serviceId);
  if (!service) return [];
  
  return service.project_ids.map(projectId => getProjectById(projectId)).filter(Boolean);
};

export const getServiceForProject = (projectId) => {
  return services.find(service => service.project_ids.includes(projectId));
};

export const getTestimonialsForProject = (projectId) => {
  return testimonials.filter(testimonial => testimonial.project_id === projectId);
};

// URL slug helpers for routing
export const getServiceSlug = (serviceId) => {
  return serviceId;
};

export const getProjectSlug = (projectId) => {
  return projectId;
};

// Legacy compatibility - returns data in the format currently used by Services.js
export const getLegacyServicesData = () => {
  return services.map(service => ({
    title: service.title,
    icon: service.icon,
    description: service.description,
    features: service.features,
    projects: service.project_ids.map(id => {
      const project = getProjectById(id);
      return project ? project.client_name : '';
    }).join(', ')
  }));
};